import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { Role, RolesRepository, RolesService } from '..'
import { Permissions } from '../../policies/policy.model'
import { initPermissions } from '../../policies'

interface RoleDetailState {
  role?: Role
  permissions: Permissions
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const ROLE_DETAIL_INITIAL_STATE: RoleDetailState = {
  permissions: initPermissions(),
  isLoading: false,
  isModified: false,
}

@Injectable()
export class RoleDetailStore {
  constructor(
    private rolesService: RolesService,
    private rolesRepository: RolesRepository,
  ) {}

  private state = signal<RoleDetailState>(ROLE_DETAIL_INITIAL_STATE)

  // Selectors
  readonly role = computed(() => this.state().role)
  readonly permissions = computed(() => this.state().permissions)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(roleId?: string): Promise<void> {
    if (!roleId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.rolesService.read$(roleId).subscribe({
        next: async (data) => {
          const extData = await this.getPermissions(data._id)
          resolve()
          this.setData(
            data,
            this.state().role?._id
              ? this.state().role?._id !== data._id
              : false,
            extData,
          )
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async save(role: Role, notify?: boolean): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.rolesService.update$(role, role._id).subscribe({
        next: async (data) => {
          resolve(true)
          this.setData(data, false)
          const permissions = this.permissions()
          if (permissions) {
            const updated = await this.updatePermissions(data._id, permissions)
            if (updated === false) {
              resolve(false)
              return
            }
          }
          if (notify === true) {
            this.rolesRepository.notify({ code: 'ROLE_SAVED' })
          }
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async create(role: Role): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.rolesService.create$(role).subscribe({
        next: async (data) => {
          resolve(true)
          this.setData(data, false)
          const permissions = this.permissions()
          if (permissions) {
            const updated = await this.updatePermissions(data._id, permissions)
            if (updated === false) {
              resolve(false)
              return
            }
          }
          this.rolesRepository.notify({ code: 'ROLE_SAVED' })
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async updatePermissions(
    roleId: string,
    permissions: Permissions,
  ): Promise<boolean> {
    return new Promise((resolve) => {
      this.rolesService.setPermissions$(roleId, permissions).subscribe({
        next: () => {
          resolve(true)
        },
        error: (error) => {
          resolve(false)
          this.rolesRepository.notify({ code: 'UNABLE_TO_SAVE_PERMISSIONS' })
          this.setError(error)
        },
      })
    })
  }

  async getPermissions(roleId?: string): Promise<Permissions | undefined> {
    if (!roleId) {
      return undefined
    }
    return new Promise((resolve, reject) => {
      this.rolesService.getPermissions$(roleId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  // Reducers

  setData(role: Role, isModified?: boolean, permissions?: Permissions) {
    this.state.set({
      ...this.state(),
      role,
      permissions: permissions ?? this.state().permissions,
      isModified: isModified ?? this.state().isModified,
      isLoading: false,
      error: undefined,
    })
  }

  setPermissions(permissions: Permissions, isModified?: boolean) {
    this.state.set({
      ...this.state(),
      permissions,
      isModified: isModified ?? this.state().isModified,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
